import { STAGES, NON_LOST_STAGES, leadsAtStage, summarizeLeadStages } from './leadStages';
import type { Stage } from '../../../shared/enums';
import type { IsoDate, Lead, Signal, Visit } from '../../../shared/types';
import { fold, searchLeads, sortByName } from './pipeline';
import { buildToday, latestVisitByLead } from './today';

/** Desktop layout helpers (D65) — pure. */

export interface SidebarCounts {
  dueFollowUps: number;
  /** null until a new-business list has been loaded on this device. */
  signals: number | null;
  pipeline: number;
  /** null when the drafts could not be counted (offline). */
  emails: number | null;
}


export const sidebarCounts = (
  leads: readonly Lead[],
  visits: readonly Visit[],
  signals: number | null,
  pendingDrafts: number | null,
  today: IsoDate,
): SidebarCounts => {
  const { overdue, dueToday } = buildToday(leads, visits, today);
  return {
    dueFollowUps: overdue.length + dueToday.length,
    signals,
    pipeline: summarizeLeadStages(leads).open,
    emails: pendingDrafts,
  };
};

export interface SearchResults {
  leads: Lead[];
  businesses: Signal[];
}

/** Leads (name, owner, address, neighborhood, phone) and new businesses (name, city) matching every
 *  word, accents and case aside; at most `limit` in all, shared between the two groups. */
export const searchAll = (
  leads: readonly Lead[],
  signals: readonly Signal[],
  query: string,
  limit = 8,
): SearchResults => {
  const terms = fold(query.trim()).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return { leads: [], businesses: [] };
  const matchedLeads = sortByName(searchLeads(leads, query));
  const matchedBiz = signals.filter((s) => {
    const hay = fold([s.business_name, s.city].filter(Boolean).join(' '));
    return terms.every((t) => hay.includes(t));
  });
  const leadRoom = Math.min(matchedLeads.length, Math.max(limit - matchedBiz.length, Math.ceil(limit / 2)));
  return { leads: matchedLeads.slice(0, leadRoom), businesses: matchedBiz.slice(0, limit - leadRoom) };
};

export interface BoardCard {
  lead: Lead;
  /** When the lead was last visited (ISO date-time), null if never. */
  lastVisit: string | null;
  /** The latest visit's next step, when it has a date. */
  next: { action: string; date: IsoDate } | null;
}

export interface BoardColumn {
  stage: Stage;
  cards: BoardCard[];
}


/** The pipeline as stage columns, names A–Z; Lost only when asked for. */
export const boardColumns = (
  leads: readonly Lead[],
  visits: readonly Visit[],
  showLost: boolean,
): BoardColumn[] => {
  const latest = latestVisitByLead(visits);
  const stages: readonly Stage[] = showLost ? STAGES : NON_LOST_STAGES;
  return stages.map((stage) => ({
    stage,
    cards: sortByName(leadsAtStage(leads, stage)).map((lead) => {
      const v = latest.get(lead.id);
      return {
        lead,
        lastVisit: v?.visited_at ?? null,
        next: v?.next_action_date
          ? { action: v.next_action?.trim() || 'Follow up', date: v.next_action_date }
          : null,
      };
    }),
  }));
};

/** ↑/↓ in a list: the neighbour, clamped; the first when nothing (or a vanished row) is selected. */
export const stepSelection = (
  ids: readonly string[],
  current: string | null,
  delta: 1 | -1,
): string | null => {
  const i = current === null ? -1 : ids.indexOf(current);
  if (i === -1) return ids[0] ?? null;
  return ids[Math.min(Math.max(i + delta, 0), ids.length - 1)] ?? null;
};

/** After a row leaves the list (added, dismissed, sent): the next one, else the one before, else none. */
export const afterRemoval = (idsBefore: readonly string[], removed: string): string | null => {
  const i = idsBefore.indexOf(removed);
  if (i === -1) return idsBefore[0] ?? null;
  return idsBefore[i + 1] ?? idsBefore[i - 1] ?? null;
};
